import { CheckIcon, Loader2Icon, TrophyIcon } from 'lucide-react';

import { cn } from '@/lib/utils';
import { scoutDefaults } from '@/modules/scout-sourcing/constants';
import type {
  ScoutCalibrationLog,
  ScoutCalibrationPhase,
  ScoutCalibrationStatus,
} from '@/modules/scout-sourcing/types';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/modules/ui/components/card/Card';

type Props = {
  log: ScoutCalibrationLog;
  championQueries?: string[];
  hideMarketSummary?: boolean;
  historical?: boolean;
};

const PHASE_LABEL: Record<ScoutCalibrationPhase, string> = {
  calibrating: 'Sizing the market…',
  sourcing: 'Sourcing candidates…',
  scoring: 'Scoring matches…',
  enhancing: 'Enriching top profiles…',
  done: 'Search complete',
  failed: 'Search failed',
};

const PHASE_DESCRIPTION: Record<ScoutCalibrationPhase, string> = {
  calibrating:
    'Scout is widening and narrowing the search until the candidate pool lands in the sweet spot.',
  sourcing: 'Pulling the matching candidates from the calibrated search.',
  scoring: 'Ranking the pool against your ideal profile.',
  enhancing: 'Enriching and re-scoring the strongest matches.',
  done: 'Candidates have been sourced and ranked.',
  failed: 'Scout could not finish this search.',
};

function StatusBadge({ status }: { status: ScoutCalibrationStatus }) {
  if (status === 'in_range') {
    return (
      <span className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-600">
        <CheckIcon className="size-3.5" />
        IN RANGE
      </span>
    );
  }
  if (status === 'too_broad') {
    return (
      <span className="text-xs font-semibold text-amber-600">TOO BROAD</span>
    );
  }
  if (status === 'too_narrow') {
    return (
      <span className="text-destructive text-xs font-semibold">TOO NARROW</span>
    );
  }
  if (status === 'overlaps_champion') {
    return (
      <span className="text-neutral-650 text-xs font-semibold">OVERLAPS</span>
    );
  }
  if (status === 'provider_error') {
    return (
      <span className="text-destructive text-xs font-semibold">
        PROVIDER ERROR
      </span>
    );
  }
  if (status === 'error') {
    return (
      <span className="text-destructive text-xs font-semibold">ERROR</span>
    );
  }
  if (status === 'query_invalid') {
    return (
      <span className="text-destructive text-xs font-semibold">ADJUSTED</span>
    );
  }
  return (
    <Loader2Icon
      className="text-neutral-650 size-3.5 animate-spin"
      aria-hidden
    />
  );
}

export function CalibrationLog({
  log,
  championQueries = [],
  hideMarketSummary = false,
  historical = false,
}: Props) {
  const isCalibrating = log.phase === 'calibrating';
  const normalizeQuery = (query: string) => query.trim().replace(/\s+/g, ' ');
  const championQuerySet = new Set(championQueries.map(normalizeQuery));
  const marketSizeText =
    log.marketSize != null
      ? `${log.marketSize.toLocaleString()} results`
      : log.phase === 'failed'
        ? '—'
        : 'calculating…';

  const showPulse = log.phase !== 'done' && log.phase !== 'failed';
  const title = historical
    ? 'Historical market calibration'
    : championQueries.length > 0 && log.phase === 'done'
      ? 'Market calibration complete'
      : PHASE_LABEL[log.phase];
  const completedSelectionReason =
    log.phase === 'done' ? log.selectionReason?.trim() : null;
  const description = historical
    ? 'These probes produced Scout’s original search. The current executable search is shown separately above.'
    : completedSelectionReason
      ? completedSelectionReason
      : championQueries.length > 0 && log.phase === 'done'
        ? 'Scout tested the market and selected champion queries for candidate preview and sourcing.'
        : PHASE_DESCRIPTION[log.phase];

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center gap-2.5">
          {showPulse && (
            <span className="relative flex size-2.5" aria-hidden>
              <span className="bg-primary absolute inline-flex size-full animate-ping rounded-full opacity-60" />
              <span className="bg-primary relative inline-flex size-2.5 rounded-full" />
            </span>
          )}
          <CardTitle>{title}</CardTitle>
        </div>
        <CardDescription>{description}</CardDescription>
      </CardHeader>

      <CardContent className="flex flex-col gap-4">
        {!hideMarketSummary && (
          <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
            <span className="text-sm font-semibold">
              Talent pool:{' '}
              <span className="tabular-nums">{marketSizeText}</span>
            </span>
            {log.targetMin != null && log.targetMax != null && (
              <span className="text-neutral-650 text-xs">
                target {log.targetMin.toLocaleString()}–
                {log.targetMax.toLocaleString()}
              </span>
            )}
            {isCalibrating && (
              <Loader2Icon
                className="text-neutral-650 size-3.5 animate-spin"
                aria-hidden
              />
            )}
          </div>
        )}

        {championQueries.length > 0 && (
          <div className="border-primary/20 bg-primary/5 text-primary rounded-md border px-3 py-2 text-xs">
            <div className="flex items-start gap-1.5">
              <TrophyIcon className="text-primary mt-0.5 size-3.5" />
              <p className="leading-relaxed">
                <span className="font-semibold">
                  {historical
                    ? 'Original champion queries:'
                    : 'Champion queries:'}
                </span>{' '}
                {historical
                  ? 'highlighted searches selected during the original calibration. They are historical and may differ from the current executable search.'
                  : `highlighted searches used for candidate preview and sourcing. Other rows show calibration probes Scout tried before choosing. Coresignal preview pages are capped, so Scout ranks and saves up to the best ${scoutDefaults.rankedPreviewCandidateCap} previewable matches from the champion queries.`}
              </p>
            </div>
          </div>
        )}

        {/* Hiring company preamble */}
        {log.hiringCompany && (
          <div className="bg-secondary-150/60 rounded-md px-3 py-2 text-xs">
            <span className="font-semibold">Hiring company:</span>{' '}
            {log.hiringCompany}
          </div>
        )}

        {/* Calibration steps */}
        {log.steps.length > 0 ? (
          <div className="border-border overflow-hidden rounded-lg border">
            <div className="text-neutral-650 border-border bg-secondary-150/40 border-b px-3 py-1.5 text-[11px] font-semibold tracking-wide uppercase">
              Calibration attempts
            </div>
            <ul>
              {log.steps.map((step) => {
                const isChampion = championQuerySet.has(
                  normalizeQuery(step.prompt),
                );
                return (
                  <li
                    key={step.index}
                    className={cn(
                      'border-border flex flex-col gap-2 border-b px-3 py-3 text-xs last:border-b-0',
                      isChampion && 'border-primary/20 bg-primary/5',
                    )}
                  >
                    <div className="flex items-center gap-3">
                      <span className="text-neutral-650 w-5 shrink-0 text-[10px] font-semibold tabular-nums">
                        #{step.index}
                      </span>
                      <span className="text-foreground flex flex-1 items-center gap-2 font-medium">
                        <span>{step.label}</span>
                        {isChampion && (
                          <span className="text-primary inline-flex items-center gap-1 rounded-full bg-white px-2 py-0.5 text-[10px] font-semibold ring-1 ring-current/20">
                            <TrophyIcon className="size-3" />
                            {historical ? 'Original champion' : 'Champion'}
                          </span>
                        )}
                      </span>
                      <span
                        className={cn(
                          'shrink-0 font-semibold tabular-nums',
                          step.count == null && 'text-neutral-650',
                        )}
                      >
                        {step.count != null ? step.count.toLocaleString() : '…'}
                      </span>
                      <span className="flex w-24 shrink-0 justify-end">
                        <StatusBadge status={step.status} />
                      </span>
                    </div>
                    <div className="flex flex-col gap-1">
                      <span className="text-neutral-650 text-[10px] font-semibold tracking-wide uppercase">
                        Search phrase
                      </span>
                      <code
                        className={cn(
                          'bg-muted text-foreground/85 block rounded-md px-3 py-2 font-mono text-[11px] leading-relaxed whitespace-pre-wrap',
                          isChampion && 'bg-white',
                        )}
                      >
                        {step.prompt}
                      </code>
                    </div>
                  </li>
                );
              })}
            </ul>
          </div>
        ) : (
          <p className="text-neutral-650 text-sm">
            Probing the market — first results coming in…
          </p>
        )}
      </CardContent>
    </Card>
  );
}
